/**
 * Supabase Client Configuration
 * Configures Supabase client for authentication and database access
 */

import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';
import { isDev } from '../utils/environment';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  logger.warn('Supabase credentials not configured. Authentication will not work.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Use default Supabase storage keys (sb-<project-ref>-auth-token)
    storage: window.localStorage,
    flowType: 'pkce',
    // Ensure session is cleared on signOut
    storageKey: `sb-${supabaseUrl.split('//')[1]?.split('.')[0]}-auth-token`
  }
});

// Export types for TypeScript
export type SupabaseClient = typeof supabase;

// Helper functions for authentication
export const auth = {
  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  // Sign up new user
  signUp: async (email: string, password: string, metadata?: Record<string, unknown>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current session
  getSession: async () => {
    const { data, error } = await supabase.auth.getSession();
    return { session: data?.session, error };
  },

  // Get current user
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Refresh session
  refreshSession: async () => {
    const { data, error } = await supabase.auth.refreshSession();
    return { session: data?.session, error };
  },

  // Reset password
  resetPassword: async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    return { data, error };
  },

  // Update password
  updatePassword: async (newPassword: string) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { data, error };
  }
};

// Database helpers using new Supabase schema
export const db = {
  // Get user profile with company membership
  getUserProfile: async (userId: string) => {
    try {
      logger.debug('getUserProfile: Starting profile query for user ID:', userId);
      
      // Step 1: Get user profile from profiles table
      if (isDev) console.log('📋 Step 1: Querying profiles table...');
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          full_name,
          phone,
          avatar_url,
          preferences,
          email_confirmed_at,
          created_at,
          updated_at
        `)
        .eq('id', userId)
        .single();
      
      if (isDev) console.log('📋 Profile query completed. Result:', { 
        hasProfile: !!profile, 
        profileEmail: profile?.email, 
        errorCode: profileError?.code,
        errorMessage: profileError?.message 
      });
      
      if (profileError || !profile) {
        if (isDev) console.error('❌ Profile query failed:', profileError);
        // Handle the case where profile doesn't exist yet
        if (profileError?.code === 'PGRST116') {
          if (isDev) console.log('🆕 No profile found - this appears to be a new user');
          return { profile: null, error: { code: 'PGRST116', message: 'No profile found' } };
        }
        return { profile: null, error: profileError };
      }
      
      if (isDev) console.log('✅ Profile found:', {
        id: profile.id,
        email: profile.email,
        full_name: profile.full_name
      });
      
      // Step 2: Get company membership with company details
      if (isDev) console.log('🏢 Step 2: Querying company membership...');
      const { data: membershipData, error: membershipError } = await supabase
        .from('company_members')
        .select(`
          id,
          role,
          status,
          joined_at,
          companies:company_id (
            id,
            name,
            slug,
            domain,
            subscription_status,
            created_at
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('joined_at', { ascending: false }) // Get most recent membership first
        .limit(1); // Handle multiple memberships gracefully
      
      // Handle array result from limit(1)
      const membership = membershipData && Array.isArray(membershipData) ? membershipData[0] : membershipData;
      
      if (isDev) console.log('🏢 Membership query completed. Result:', { 
        hasMembership: !!membership, 
        membershipRole: membership?.role,
        companyName: membership?.companies?.name,
        errorCode: membershipError?.code,
        errorMessage: membershipError?.message 
      });
      
      if (membershipError || !membership) {
        if (isDev) console.log('⚠️ No active company membership found:', membershipError);

        // Some environments have strict RLS and return 403/permission denied when
        // the membership policy is too restrictive. Treat these as "no membership yet"
        // so the app can proceed to onboarding instead of failing the login flow.
        const permissionDenied =
          membershipError?.code === '42501' ||
          /permission denied/i.test(String(membershipError?.message || ''));

        // Handle the case where user has no company membership yet
        if (membershipError?.code === 'PGRST116' || permissionDenied || (!membership && !membershipError)) {
          if (isDev) console.log('🆕 User has no company membership - will check system admin status via backend...');

          // Backend handles system admin check using service role key (bypasses RLS)
          // Frontend should not query system_admins directly as it triggers RLS permission errors
          const isSystemAdmin = false; // Backend will provide correct value via /api/v1/auth/profile
          if (isDev) console.log('🔐 System admin check delegated to backend');

          const newUserProfile = {
            id: profile.id,
            email: profile.email,
            name: profile.full_name || profile.email.split('@')[0],
            full_name: profile.full_name,
            phone: profile.phone,
            avatar_url: profile.avatar_url,
            preferences: profile.preferences || {},
            email_confirmed_at: profile.email_confirmed_at,
            created_at: profile.created_at,
            updated_at: profile.updated_at,

            // Role based on system admin status
            role: isSystemAdmin ? 'superadmin' : 'user',
            roles: [isSystemAdmin ? 'superadmin' : 'user'],
            status: 'active',
            joined_at: null,

            // Empty company info (unless system admin)
            org_id: '',
            orgId: '',
            companyId: '',
            organizationName: undefined,
            organizations: null,

            // Computed flags
            isSuperAdmin: isSystemAdmin,
            isAdmin: isSystemAdmin,
            isAgent: false
          };
          
          if (isDev) console.log('✅ New user profile created (no membership):', newUserProfile);
          return { profile: newUserProfile, error: null };
        }
        
        if (isDev) console.error('💥 Membership error not handled:', membershipError);
        return { profile: null, error: membershipError };
      }
      
      if (isDev) console.log('✅ Company membership found:', {
        role: membership.role,
        companyName: membership.companies?.name,
        companyId: membership.companies?.id
      });

      // Step 3: System admin status determined by backend
      if (isDev) console.log('🔐 Step 3: System admin check delegated to backend...');
      // Backend uses service role key to query system_admins (bypasses RLS)
      // Frontend querying directly causes 500 errors due to RLS policies
      const isSystemAdmin = false; // Backend will set this correctly

      if (isDev) console.log('🔐 System admin check will be handled by /api/v1/auth/profile endpoint');

      // Step 4: Combine profile and company data
      if (isDev) console.log('🔧 Step 4: Building complete profile...');
      const fullProfile = {
        id: profile.id,
        email: profile.email,
        name: profile.full_name || profile.email.split('@')[0],
        full_name: profile.full_name,
        phone: profile.phone,
        avatar_url: profile.avatar_url,
        preferences: profile.preferences || {},
        email_confirmed_at: profile.email_confirmed_at,
        created_at: profile.created_at,
        updated_at: profile.updated_at,
        
        // Company membership info
        role: isSystemAdmin ? 'superadmin' : membership.role,
        roles: [isSystemAdmin ? 'superadmin' : membership.role],
        status: membership.status,
        joined_at: membership.joined_at,

        // Company info
        org_id: membership.companies.id,
        orgId: membership.companies.id,
        companyId: membership.companies.id,
        organizationName: membership.companies.name,
        organizations: membership.companies,

        // Computed flags
        isSuperAdmin: isSystemAdmin || membership.role === 'superadmin',
        isAdmin: isSystemAdmin || membership.role === 'admin',
        isAgent: membership.role === 'agent',
        
        can_view_breakdown: membership.companies?.settings?.user_permissions?.[profile.id]?.can_view_breakdown ?? false
      };
      
      if (isDev) console.log('🎉 Complete profile successfully built:', {
        id: fullProfile.id,
        email: fullProfile.email,
        role: fullProfile.role,
        organizationName: fullProfile.organizationName
      });
      
      return { profile: fullProfile, error: null };
    } catch (error) {
      if (isDev) {
        if (isDev) console.error('💥 getUserProfile: Unexpected error:', error);
        if (isDev) console.error('💥 Error stack:', error instanceof Error ? error.stack : 'No stack trace available');
      }
      return { profile: null, error };
    }
  },

  // Update user profile
  updateUserProfile: async (userId: string, updates: Record<string, unknown>) => {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    
    return { profile: data, error };
  },

  // Get company details
  getCompany: async (companyId: string) => {
    const { data, error } = await supabase
      .from('companies')
      .select(`
        id,
        name,
        slug,
        domain,
        subscription_status,
        created_at,
        updated_at
      `)
      .eq('id', companyId)
      .single();
    
    return { organization: data, error };
  },

  // Legacy compatibility - alias for getCompany
  getOrganization: async (orgId: string) => {
    return await db.getCompany(orgId);
  },

  // Get subscription (simplified - using company subscription_status)
  getSubscription: async (companyId: string) => {
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('subscription_status')
        .eq('id', companyId)
        .single();
      
      if (error || !company) {
        return { subscription: null, error };
      }
      
      // Create a simple subscription object based on company status
      const subscription = {
        id: companyId,
        company_id: companyId,
        status: company.subscription_status,
        billing_plans: {
          plan_name: company.subscription_status === 'active' ? 'professional' : 'trial',
          display_name: company.subscription_status === 'active' ? 'Professional' : 'Trial'
        }
      };
      
      return { subscription, error: null };
    } catch (error) {
      console.error('Error in getSubscription:', error);
      return { subscription: null, error };
    }
  },

  // Create user profile (for new signups)
  createUserProfile: async (userId: string, email: string, fullName?: string) => {
    try {
      // Create profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          email: email,
          full_name: fullName,
          preferences: {
            theme: 'system',
            notifications: true,
            language: 'en'
          }
        })
        .select()
        .single();
      
      if (profileError) {
        return { profile: null, error: profileError };
      }
      
      // Find or create a default company for the user
      // For now, we'll need them to be invited to a company
      // or create their own company separately
      
      return { profile, error: null };
    } catch (error) {
      console.error('Error creating user profile:', error);
      return { profile: null, error };
    }
  }
};

export default supabase;
